/**
 * Vista: Detalle/Edición de Recepción Visual
 * Patrón similar a PNCDetalle: editable cuando está abierta
 */

import recepcionService from '../services/recepcion.service.js';
import router from '../router/router.js';
import permisos from '../services/permissions.service.js';
import { pickImage } from '../utils/mediaChooser.js';
import { compressImage } from '../utils/imageCompressor.js';
import { normalizers, attachNormalizer } from '../utils/inputNormalizer.js';
import { subirVideoRecepcion } from '../utils/videoUploader.js';
import { fechaCorta } from '../utils/fechas.js';
import { danosRecepcionHtml, iniciarDanosRecepcion, leerDanosRecepcion, validarDanosRecepcion, danosRecepcionLecturaHtml, danosParaGuardar, cargarCatalogosDanos } from '../utils/danosRecepcion.js';

export default {
  data: {
    recepcion: null,
    fotosNuevas: [],
    hasUnsavedChanges: false
  },

  async render() {
    return `
      <div class="card">
        <div class="card-header" style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem;">
          <h2 class="card-title">Recepción Visual</h2>
          <div id="headerButtons" style="display: flex; gap: 0.5rem;">
            <button class="btn btn-secondary" onclick="router.navigate('/almacen/recepciones')">← Volver</button>
          </div>
        </div>
        <div id="recContent" style="padding: 1rem;">
          <div style="text-align: center; padding: 2rem;">
            <div class="spinner"></div>
            <p>Cargando recepción...</p>
          </div>
        </div>
      </div>
    `;
  },

  async afterRender() {
    window.detalleRecepcionView = this;
    this.injectStyles();

    const path = window.location.pathname;
    const id = path.split('/').pop();
    await this.cargarRecepcion(id);
  },

  injectStyles() {
    if (document.getElementById('rec-detalle-styles')) return;
    const style = document.createElement('style');
    style.id = 'rec-detalle-styles';
    style.textContent = `
      .rec-tabs-bar { display: flex; padding: 0; background: transparent; border-bottom: 2px solid #e5e7eb; margin-bottom: 1rem; }
      .rec-tab { flex: 1; padding: 0.6rem 1rem; border: none; border-bottom: 3px solid transparent; margin-bottom: -2px; background: transparent; color: #6b7280; font-size: 0.875rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; text-align: center; }
      .rec-tab:hover:not(.active) { color: #374151; border-bottom-color: #d1d5db; }
      .rec-tab.active { color: var(--primary); font-weight: 600; border-bottom-color: var(--primary); }
      @media (max-width: 640px) {
        .rec-tabs-bar { display: grid; grid-template-columns: 1fr 1fr; gap: 0.3rem; padding: 0.3rem; }
        .rec-tab { padding: 0.4rem 0.5rem; font-size: 0.75rem; }
        .rec-edit-grid-2 { grid-template-columns: 1fr !important; }
        .rec-edit-grid-3 { grid-template-columns: 1fr !important; }
      }
    `;
    document.head.appendChild(style);
  },

  async cargarRecepcion(id) {
    try {
      const recepcion = await recepcionService.obtenerPorId(id);
      this.data.recepcion = recepcion;
      // Limpiar todos los buffers de archivos pendientes (evita doble upload si se guarda otra vez)
      this.data.fotosNuevas = [];
      this.data.nuevoVideo = null;
      this.data.fotoNumSerieNueva = null;
      this.data.fotoNumMotorNueva = null;
      this.data.fotoHorometroNueva = null;
      this.data.hasUnsavedChanges = false;

      // Resetear signaturePads previos: si quedaran vivos en memoria, al guardar
      // de nuevo re-subirían firmas ya persistidas.
      this.signaturePadAlmacen = null;
      this.signaturePadRecibio = null;

      // Botones del header según estado
      const headerButtons = document.getElementById('headerButtons');
      const titleEl = document.querySelector('.card-title');
      if (titleEl) titleEl.textContent = 'Recepción Visual';

      if (recepcion.completada) {
        headerButtons.innerHTML = `
          <button class="btn btn-secondary" onclick="router.navigate('/almacen/recepciones')">← Volver</button>
          ${permisos.canPrint('recepciones') ? `<button class="btn btn-primary" onclick="window.detalleRecepcionView.imprimir(${recepcion.id})">🖨️ Imprimir</button>` : ''}
        `;
      } else {
        headerButtons.innerHTML = `
          <button class="btn btn-secondary" onclick="router.navigate('/almacen/recepciones')">← Volver</button>
        `;
      }

      // Siempre renderizar el mismo formulario
      this.renderFormularioEdicion();

      // Si está completada, deshabilitar todo
      if (recepcion.completada) {
        this._disableForm();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar la recepción');
      document.getElementById('recContent').innerHTML = `
        <div style="text-align: center; padding: 2rem;">
          <p style="color: var(--danger);">No se pudo cargar la recepción.</p>
          <button class="btn btn-primary" onclick="router.navigate('/almacen/recepciones')">Volver a la lista</button>
        </div>
      `;
    }
  },

  renderFormularioEdicion() {
    const r = this.data.recepcion;
    const container = document.getElementById('recContent');
    const checklist = r.checklist || {};
    // Subir/reemplazar video se gobierna con su propio permiso `subir_video`
    // (compartido, independiente de `editar`). El video sigue siendo
    // obligatorio para completar la recepción.
    const puedeSubirVideo = permisos.can('recepciones', 'subir_video');

    // Badge contextual según tipo de recepción
    let contextoBadge = '';
    if (r.tipo_recepcion === 'reingreso') {
      const motivos = {
        'expo_demo': 'Expo / Demo',
        'falta_pago': 'Devolución por falta de pago',
        'garantia': 'Devolución por garantía',
        'prestamo': 'Préstamo devuelto',
        'otro': 'Otro'
      };
      const motivoTxt = motivos[r.motivo_reingreso] || r.motivo_reingreso || '-';
      contextoBadge = `
        <div style="padding: 0.9rem; background: #fff9eb; border: 1px solid var(--warning, #f59e0b); border-radius: 8px; margin-bottom: 1.5rem;">
          <div style="font-weight: 600; color: #92400e;">🔄 Reingreso</div>
          <div style="font-size: 0.9rem; color: var(--gray-700); margin-top: 0.25rem;">
            Motivo: <strong>${motivoTxt}</strong>
          </div>
        </div>
      `;
    } else if (r.pedido_compra_detalle_unidad_id && r.pedido_folio) {
      contextoBadge = `
        <div style="padding: 0.9rem; background: #eef2ff; border: 1px solid #6366f1; border-radius: 8px; margin-bottom: 1.5rem;">
          <div style="font-weight: 600; color: #3730a3;">📦 Recepción inicial vinculada a pedido</div>
          <div style="font-size: 0.9rem; color: var(--gray-700); margin-top: 0.25rem;">
            ${r.pedido_folio} · ${r.pedido_proveedor_nombre || 'Proveedor'}
          </div>
        </div>
      `;
    }

    // Badge de PNC asociado (si aplica)
    let pncBadge = '';
    if (r.pnc_id) {
      const estadoPnc = r.pnc_estado || 'abierto';
      const colorEstado = estadoPnc === 'cerrado' ? '#16a34a' : estadoPnc === 'en_proceso' ? '#d97706' : '#dc2626';
      const puedeVerPnc = permisos.canView('pnc');
      pncBadge = `
        <div style="padding: 0.9rem; background: #fef2f2; border: 1px solid #ef4444; border-radius: 8px; margin-bottom: 1.5rem;">
          <div style="font-weight: 600; color: #991b1b;">⚠️ Producto No Conforme asociado</div>
          <div style="font-size: 0.9rem; color: var(--gray-700); margin-top: 0.25rem;">
            PNC-${r.pnc_id} ·
            <span style="color: ${colorEstado}; font-weight: 600; text-transform: capitalize;">${estadoPnc.replace('_', ' ')}</span>
            ${puedeVerPnc ? `<button style="margin-left: 0.5rem; padding: 0.2rem 0.6rem; background: white; border: 1px solid #ef4444; border-radius: 4px; cursor: pointer; color: #991b1b; font-size: 0.8rem;"
                    onclick="router.navigate('/calidad/pnc/${r.pnc_id}')">Ver PNC</button>` : ''}
          </div>
        </div>
      `;
    }
    contextoBadge = contextoBadge + pncBadge;

    container.innerHTML = `
      ${contextoBadge}
      <!-- Datos del Tractor -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div class="rec-edit-grid-2" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Número de Serie</label>
            <input type="text" class="form-input" value="${r.num_serie}" readonly style="background: var(--gray-50);" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Marca</label>
            <input type="text" class="form-input" value="${r.marca}" readonly style="background: var(--gray-50);" />
          </div>
        </div>
        <div class="rec-edit-grid-2" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Modelo</label>
            <input type="text" class="form-input" value="${r.modelo}" readonly style="background: var(--gray-50);" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Almacén</label>
            <input type="text" class="form-input" value="${r.almacen_codigo ? r.almacen_codigo + ' - ' + r.almacen_nombre : r.almacen_nombre || 'N/A'}" readonly style="background: var(--gray-50);" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Fecha de Recepción</label>
            <input type="date" id="editFechaRecepcion" class="form-input" value="${r.fecha_recepcion ? r.fecha_recepcion.split('T')[0] : ''}" />
          </div>
        </div>
      </div>

      <!-- Documentación -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Documentación <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <!-- Certificado de Origen -->
        <div style="display: flex; align-items: center; padding: 0.6rem 0.75rem; border: 1px solid var(--gray-200); border-radius: var(--border-radius); background: white; margin-bottom: 1rem;">
          <label style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer; margin: 0;">
            <input type="checkbox" id="editCertificadoOrigen" ${r.certificado_origen ? 'checked' : ''} />
            <span style="font-size: 0.875rem;">Certificado de Origen</span>
          </label>
        </div>

        <!-- Guía, Factura, Pedimento -->
        <div class="rec-edit-grid-2" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Guía de Embarque</label>
            <input type="text" id="editGuiaEmbarque" class="form-input" value="${r.guia_embarque || ''}" placeholder="" style="text-transform: uppercase;" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Factura</label>
            <input type="text" id="editFactura" class="form-input" value="${r.factura || ''}" placeholder="" style="text-transform: uppercase;" />
          </div>
        </div>
        <div style="margin-top: 0.5rem;">
          <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Pedimento${r.tipo_recepcion !== 'reingreso' ? ' *' : ''}</label>
          <input type="text" id="editPedimento" class="form-input" value="${r.pedimento || ''}" placeholder="${r.tipo_recepcion !== 'reingreso' ? 'Obligatorio para compras' : 'No aplica en reingreso'}" style="text-transform: uppercase;" />
        </div>
      </div>

      <!-- Tabs -->
      <div class="rec-tabs-bar">
        <button class="rec-tab active" onclick="window.detalleRecepcionView.showTab('checklist')">Checklist</button>
        <button class="rec-tab" onclick="window.detalleRecepcionView.showTab('observaciones')">Observaciones</button>
        <button class="rec-tab" onclick="window.detalleRecepcionView.showTab('evidencias')">Evidencias${r.fotos && r.fotos.length > 0 ? ` (${r.fotos.length})` : ''}</button>
        <button class="rec-tab" onclick="window.detalleRecepcionView.showTab('firmas')">Firmas</button>
      </div>

      <!-- Tab: Checklist -->
      <div class="rec-tab-content" id="tab-checklist" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
        <div class="checklist-grid">
          ${this.renderEditCheckItem('documentacion', 'Documentación', checklist.documentacion)}
          ${this.renderEditCheckItem('tren_motriz', 'Tren Motriz', checklist.tren_motriz)}
          ${this.renderEditCheckItem('emblema', 'Emblema', checklist.emblema)}
          ${this.renderEditCheckItem('estabilizadores', 'Estabilizadores', checklist.estabilizadores)}
          ${this.renderEditCheckItem('salpicaderas', 'Salpicaderas', checklist.salpicaderas)}
          ${this.renderEditCheckItem('mofle_escape', 'Mofle o Escape', checklist.mofle_escape)}
          ${this.renderEditCheckItem('llantas_traseras', 'Llantas Traseras', checklist.llantas_traseras)}
          ${this.renderEditCheckItem('barra_tiro', 'Barra de Tiro', checklist.barra_tiro)}
          ${this.renderEditCheckItem('llantas_delanteras', 'Llantas Delanteras', checklist.llantas_delanteras)}
          ${this.renderEditCheckItem('brazos_levante', 'Brazos de Levante', checklist.brazos_levante)}
          ${this.renderEditCheckItem('caja_herramientas', 'Caja de Herramientas', checklist.caja_herramientas)}
          ${this.renderEditCheckItem('contrapesos_traseros', 'Contrapesos Traseros', checklist.contrapesos_traseros)}
          ${this.renderEditCheckItem('caja_tornilleria', 'Caja de Tornillería', checklist.caja_tornilleria)}
          ${this.renderEditCheckItem('contrapesos_delanteros', 'Contrapesos Delanteros', checklist.contrapesos_delanteros)}
          ${this.renderEditCheckItem('bastidor_bateria', 'Bastidor con Batería', checklist.bastidor_bateria)}
          ${this.renderEditCheckItem('rops_toldo', 'ROPS / Toldo', checklist.rops_toldo)}
          ${this.renderEditCheckItem('eje_delantero', 'Eje Delantero', checklist.eje_delantero)}
          ${this.renderEditCheckItem('cabina', 'Cabina', checklist.cabina)}
          ${this.renderEditCheckItem('cubierta_pto', 'Cubierta de PTO', checklist.cubierta_pto)}
        </div>
      </div>

      <!-- Tab: Observaciones -->
      <div class="rec-tab-content" id="tab-observaciones" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <div style="margin-bottom: 1rem;">
          <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Estado General</label>
          <select id="editEstadoGeneral" class="form-select">
            <option value="excelente" ${r.estado_general === 'excelente' ? 'selected' : ''}>Excelente</option>
            <option value="bueno" ${r.estado_general === 'bueno' ? 'selected' : ''}>Bueno</option>
            <option value="regular" ${r.estado_general === 'regular' ? 'selected' : ''}>Regular</option>
            <option value="malo" ${r.estado_general === 'malo' ? 'selected' : ''}>Malo</option>
          </select>
        </div>
        ${r.completada ? danosRecepcionLecturaHtml(r) : danosRecepcionHtml('edit', r)}
        <div>
          <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Observaciones (nota)</label>
          <textarea id="editObservaciones" class="form-input" rows="4" placeholder="" style="text-transform: uppercase;">${r.observaciones || ''}</textarea>
          <p style="font-size: 0.75rem; color: var(--gray-400); margin: 0.25rem 0 0;">Es sólo una nota: no abre PNC. Los daños van arriba, en «¿Hay daño?».</p>
        </div>
      </div>

      <!-- Tab: Evidencias -->
      <div class="rec-tab-content" id="tab-evidencias" style="display: none;">

        <!-- Sección 1: Fotos de Identificación -->
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
          <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Fotos de Identificación <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
          <div class="rec-edit-grid-3" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem;">
            <!-- Foto Número de Serie -->
            <div>
              <label class="form-label">Número de Serie *</label>
              <div id="foto-numserie-box" style="aspect-ratio: 4/3; border: 2px dashed var(--gray-300); border-radius: 8px; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; overflow: hidden;"
                   onclick="window.detalleRecepcionView.pickFotoIdentificacion('numserie')">
                ${r.foto_num_serie_url
                  ? `<img src="${r.foto_num_serie_url}" style="width: 100%; height: 100%; object-fit: cover;" />`
                  : `<div style="text-align: center; color: var(--gray-400);">
                      <div style="font-size: 2rem;">📷</div>
                      <p style="margin: 0.5rem 0 0; font-size: 0.8rem;">Toque para capturar</p>
                    </div>`
                }
              </div>
            </div>
            <!-- Foto Número de Motor -->
            <div>
              <label class="form-label">Número de Motor *</label>
              <div id="foto-nummotor-box" style="aspect-ratio: 4/3; border: 2px dashed var(--gray-300); border-radius: 8px; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; overflow: hidden;"
                   onclick="window.detalleRecepcionView.pickFotoIdentificacion('nummotor')">
                ${r.foto_num_motor_url
                  ? `<img src="${r.foto_num_motor_url}" style="width: 100%; height: 100%; object-fit: cover;" />`
                  : `<div style="text-align: center; color: var(--gray-400);">
                      <div style="font-size: 2rem;">📷</div>
                      <p style="margin: 0.5rem 0 0; font-size: 0.8rem;">Toque para capturar</p>
                    </div>`
                }
              </div>
            </div>
            <!-- Foto Horómetro -->
            <div>
              <label class="form-label">Horómetro *</label>
              <div id="foto-horometro-box" style="aspect-ratio: 4/3; border: 2px dashed var(--gray-300); border-radius: 8px; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; overflow: hidden;"
                   onclick="window.detalleRecepcionView.pickFotoIdentificacion('horometro')">
                ${r.foto_horometro_url
                  ? `<img src="${r.foto_horometro_url}" style="width: 100%; height: 100%; object-fit: cover;" />`
                  : `<div style="text-align: center; color: var(--gray-400);">
                      <div style="font-size: 2rem;">📷</div>
                      <p style="margin: 0.5rem 0 0; font-size: 0.8rem;">Toque para capturar</p>
                    </div>`
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Sección 2: Video -->
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
          <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Video de Inspección <span style="color: var(--danger); font-size: 1rem;">*</span> <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
          ${r.video_url ? `
            <video controls style="width: 100%; max-height: 400px; border-radius: 8px;" preload="metadata">
              <source src="${r.video_url}" type="video/mp4">
              <source src="${r.video_url}" type="video/quicktime">
            </video>
            ${puedeSubirVideo && !r.completada ? `
              <div style="margin-top: 0.75rem;">
                <button type="button" class="btn btn-secondary" style="font-size: 0.85rem;"
                        onclick="window.detalleRecepcionView.pickVideoInspeccion()">Reemplazar video</button>
              </div>
            ` : ''}
          ` : (puedeSubirVideo && !r.completada ? `
            <div class="foto-placeholder" style="width: 150px; height: 150px; flex-direction: column; gap: 0.25rem;"
                 onclick="window.detalleRecepcionView.pickVideoInspeccion()">
              <span class="foto-placeholder-icon">🎥</span>
              <span style="font-size: 0.75rem; color: var(--gray-400);">Video</span>
            </div>
            <div id="edit-video-preview"></div>
          ` : `
            <p style="color: var(--gray-500); font-size: 0.875rem; margin: 0;">${puedeSubirVideo ? 'Sin video de inspección.' : 'No tienes permiso para subir el video. Solicítalo al administrador si lo necesitas.'}</p>
          `)}
        </div>

        <!-- Sección 3: Fotos del Tractor -->
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px;">
          <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Fotos del Tractor <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
          <div id="edit-fotos-grid" class="fotos-grid-new"></div>
        </div>

      </div>

      <!-- Tab: Firmas -->
      <div class="rec-tab-content" id="tab-firmas" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <div id="firmasContainer" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;" class="rec-edit-grid-2">
        </div>
      </div>

      <!-- Indicador de cambios sin guardar -->
      <div id="unsavedIndicator" style="display: none; background: var(--warning); color: white; padding: 0.5rem 1rem; border-radius: 8px; margin-bottom: 1rem; text-align: center;">
        Hay cambios sin guardar
      </div>

      <!-- Botones -->
      <div style="display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1.5rem;">
        <button class="btn btn-secondary" onclick="window.detalleRecepcionView.volver()">Cancelar</button>
        ${permisos.canEdit('recepciones') ? `<button class="btn btn-success" id="guardarEdicionBtn" onclick="window.detalleRecepcionView.guardarCambios()">Guardar Cambios</button>` : ''}
        ${permisos.can('recepciones', 'cerrar') ? `<button class="btn btn-primary" onclick="window.detalleRecepcionView.completarRecepcion()">Completar</button>` : ''}
      </div>
    `;

    // Inicializar eventos
    this.initEditEvents();
  },

  // renderVistaLectura eliminado — se usa renderFormularioEdicion + _disableForm
  _legacy_renderVistaLectura() {
    const r = this.data.recepcion;
    const container = document.getElementById('recContent');
    const checklist = r.checklist || {};
    const checkIcon = (v) => v ? '✅' : '❌';

    container.innerHTML = `
      <!-- Datos generales -->
      <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
        <h3 style="margin-bottom: 1rem;">Datos del Tractor</h3>
        <div class="rec-edit-grid-2" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;">
          <div class="info-item"><label>Número de Serie</label><strong>${r.num_serie}</strong></div>
          <div class="info-item"><label>Marca</label><strong>${r.marca}</strong></div>
          <div class="info-item"><label>Modelo</label><strong>${r.modelo}</strong></div>
          <div class="info-item"><label>Fecha de Recepción</label><strong>${fechaCorta(r.fecha_recepcion)}</strong></div>
        </div>
      </div>

      <!-- Documentación -->
      <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
        <h3 style="margin-bottom: 1rem;">Documentación</h3>
        <div class="rec-edit-grid-2" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;">
          <div class="info-item"><label>Certificado de Origen</label><strong>${r.certificado_origen ? '✅ Sí' : '❌ No'}</strong></div>
          <div class="info-item"><label>Guía de Embarque</label><strong>${r.guia_embarque || 'N/A'}</strong></div>
          <div class="info-item"><label>Factura</label><strong>${r.factura || 'N/A'}</strong></div>
          <div class="info-item"><label>Pedimento</label><strong>${r.pedimento || 'N/A'}</strong></div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="rec-tabs-bar">
        <button class="rec-tab active" onclick="window.detalleRecepcionView.showTab('checklist')">Checklist</button>
        <button class="rec-tab" onclick="window.detalleRecepcionView.showTab('observaciones')">Observaciones</button>
        <button class="rec-tab" onclick="window.detalleRecepcionView.showTab('evidencias')">Evidencias${r.fotos && r.fotos.length > 0 ? ` (${r.fotos.length})` : ''}</button>
        <button class="rec-tab" onclick="window.detalleRecepcionView.showTab('firmas')">Firmas</button>
      </div>

      <!-- Tab: Checklist (solo lectura) -->
      <div class="rec-tab-content" id="tab-checklist" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;">
          ${this.renderReadCheckItem('Documentación', checklist.documentacion)}
          ${this.renderReadCheckItem('Tren Motriz', checklist.tren_motriz)}
          ${this.renderReadCheckItem('Emblema', checklist.emblema)}
          ${this.renderReadCheckItem('Estabilizadores', checklist.estabilizadores)}
          ${this.renderReadCheckItem('Salpicaderas', checklist.salpicaderas)}
          ${this.renderReadCheckItem('Mofle o Escape', checklist.mofle_escape)}
          ${this.renderReadCheckItem('Llantas Traseras', checklist.llantas_traseras)}
          ${this.renderReadCheckItem('Barra de Tiro', checklist.barra_tiro)}
          ${this.renderReadCheckItem('Llantas Delanteras', checklist.llantas_delanteras)}
          ${this.renderReadCheckItem('Brazos de Levante', checklist.brazos_levante)}
          ${this.renderReadCheckItem('Caja de Herramientas', checklist.caja_herramientas)}
          ${this.renderReadCheckItem('Contrapesos Traseros', checklist.contrapesos_traseros)}
          ${this.renderReadCheckItem('Caja de Tornillería', checklist.caja_tornilleria)}
          ${this.renderReadCheckItem('Contrapesos Delanteros', checklist.contrapesos_delanteros)}
          ${this.renderReadCheckItem('Bastidor con Batería', checklist.bastidor_bateria)}
          ${this.renderReadCheckItem('ROPS / Toldo', checklist.rops_toldo)}
          ${this.renderReadCheckItem('Eje Delantero', checklist.eje_delantero)}
          ${this.renderReadCheckItem('Cabina', checklist.cabina)}
          ${this.renderReadCheckItem('Cubierta de PTO', checklist.cubierta_pto)}
        </div>
      </div>

      <!-- Tab: Observaciones (solo lectura) -->
      <div class="rec-tab-content" id="tab-observaciones" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <div class="info-item" style="margin-bottom: 1rem;"><label>Estado General</label><strong><span class="badge badge-${this.getBadgeClass(r.estado_general)}">${(r.estado_general || 'bueno').toUpperCase()}</span></strong></div>
        ${danosRecepcionLecturaHtml(r)}
        ${r.observaciones ? `<div class="info-item"><label>Observaciones</label><p style="background: var(--gray-50); padding: 1rem; border-radius: 8px; margin: 0.5rem 0 0;">${r.observaciones}</p></div>` : '<p style="color: var(--gray-500); text-align: center; padding: 1rem;">Sin observaciones registradas.</p>'}
      </div>

      <!-- Tab: Evidencias (solo lectura) -->
      <div class="rec-tab-content" id="tab-evidencias" style="display: none;">
        <!-- Fotos de identificación -->
        ${r.foto_num_serie_url || r.foto_num_motor_url ? `
          <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Fotos de Identificación <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <div class="rec-edit-grid-2" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;">
              <div>
                <label class="form-label">Número de Serie</label>
                <div style="aspect-ratio: 4/3; border: 2px solid var(--gray-300); border-radius: 8px; overflow: hidden; background: white; cursor: pointer;"
                     onclick="window.detalleRecepcionView.verFoto('${r.foto_num_serie_url}', 'Núm. Serie')">
                  ${r.foto_num_serie_url
                    ? `<img src="${r.foto_num_serie_url}" style="width: 100%; height: 100%; object-fit: cover;" />`
                    : `<p style="text-align: center; color: var(--gray-500); padding: 2rem;">Sin foto</p>`}
                </div>
              </div>
              <div>
                <label class="form-label">Número de Motor</label>
                <div style="aspect-ratio: 4/3; border: 2px solid var(--gray-300); border-radius: 8px; overflow: hidden; background: white; cursor: pointer;"
                     onclick="window.detalleRecepcionView.verFoto('${r.foto_num_motor_url}', 'Núm. Motor')">
                  ${r.foto_num_motor_url
                    ? `<img src="${r.foto_num_motor_url}" style="width: 100%; height: 100%; object-fit: cover;" />`
                    : `<p style="text-align: center; color: var(--gray-500); padding: 2rem;">Sin foto</p>`}
                </div>
              </div>
            </div>
          </div>
        ` : ''}

        <!-- Video -->
        ${r.video_url ? `
          <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Video de Inspección <span style="color: var(--danger); font-size: 1rem;">*</span> <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <video controls style="width: 100%; max-height: 400px; border-radius: 8px;" preload="metadata">
              <source src="${r.video_url}" type="video/mp4">
            </video>
          </div>
        ` : ''}

        <!-- Fotos del tractor -->
        <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px;">
          <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Fotos del Tractor${r.fotos && r.fotos.length > 0 ? ` (${r.fotos.length})` : ''} <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
          ${r.fotos && r.fotos.length > 0 ? `
            <div class="fotos-grid">
              ${r.fotos.map((foto, i) => `
                <div class="foto-item" onclick="window.detalleRecepcionView.verFoto('${foto}', ${i + 1})">
                  <img src="${foto}" alt="Foto ${i + 1}" onerror="this.src='data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22%3E%3Crect fill=%22%23ddd%22 width=%22200%22 height=%22200%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 fill=%22%23999%22 text-anchor=%22middle%22 dy=%22.3em%22%3EFoto ${i + 1}%3C/text%3E%3C/svg%3E'" />
                  <div class="foto-overlay"><span>📷 Foto ${i + 1}</span></div>
                </div>
              `).join('')}
            </div>
          ` : '<p style="color: var(--gray-500); text-align: center;">Sin fotos registradas.</p>'}
        </div>
      </div>

      <!-- Tab: Firmas (solo lectura) -->
      <div class="rec-tab-content" id="tab-firmas" style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; display: none;">
        <div class="rec-edit-grid-2" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 3rem;">
          <div style="text-align: center; padding: 0.5rem;">
            ${r.responsable_almacen_firma_url
              ? `<img src="${r.responsable_almacen_firma_url}" style="width: 100%; max-height: 150px; object-fit: contain;" onerror="this.style.display='none'" />`
              : '<div style="min-height: 150px; display: flex; align-items: center; justify-content: center;"><span style="color: var(--text-secondary);">Sin firma</span></div>'
            }
            <hr style="border: none; border-top: 3px solid #000; margin: 0.5rem 0;" />
            <p style="margin: 0.4rem 0 0; font-weight: 700; font-size: 0.95rem; text-transform: uppercase;">${r.responsable_almacen_nombre || ''}</p>
            <p style="margin: 0.25rem 0 0; color: var(--text-secondary); font-size: 0.85rem;">Elaboró</p>
          </div>
          <div style="text-align: center; padding: 0.5rem;">
            ${r.recibio_firma_url
              ? `<img src="${r.recibio_firma_url}" style="width: 100%; max-height: 150px; object-fit: contain;" onerror="this.style.display='none'" />`
              : '<div style="min-height: 150px; display: flex; align-items: center; justify-content: center;"><span style="color: var(--text-secondary);">Sin firma</span></div>'
            }
            <hr style="border: none; border-top: 3px solid #000; margin: 0.5rem 0;" />
            <p style="margin: 0.4rem 0 0; font-weight: 700; font-size: 0.95rem; text-transform: uppercase;">${r.recibio_nombre || ''}</p>
            <p style="margin: 0.25rem 0 0; color: var(--text-secondary); font-size: 0.85rem;">Jefe de Almacén</p>
          </div>
        </div>
      </div>
    `;
  },

  renderReadCheckItem(label, value) {
    const icon = value ? '✅' : '❌';
    return `
      <div style="display: flex; align-items: center; gap: 0.5rem; padding: 0.4rem 0.5rem; background: var(--gray-50); border-radius: 4px;">
        <span>${icon}</span>
        <span style="font-size: 0.875rem;">${label}</span>
      </div>
    `;
  },

  async completarRecepcion() {
    // Primero guardar cambios pendientes
    if (this._hasUnsaved) {
      toast.warning('Guarde los cambios antes de completar la recepción');
      return;
    }

    const r = this.data.recepcion;

    // Validar checklist completo.
    // cabina y rops_toldo son mutuamente excluyentes según el modelo del tractor
    // (solo se sabe en la inspección). Se requiere AL MENOS UNO de los dos.
    const checklist = r.checklist || {};
    const checklistFields = [
      'documentacion', 'emblema', 'salpicaderas', 'llantas_traseras', 'llantas_delanteras',
      'caja_herramientas', 'caja_tornilleria', 'bastidor_bateria', 'eje_delantero', 'cubierta_pto',
      'tren_motriz', 'estabilizadores', 'mofle_escape', 'barra_tiro', 'brazos_levante',
      'contrapesos_traseros', 'contrapesos_delanteros'
    ];
    if (checklistFields.some(f => !checklist[f])) {
      this.showTab('checklist');
      toast.error('Complete todos los ítems del checklist');
      return;
    }
    if (!checklist.cabina && !checklist.rops_toldo) {
      this.showTab('checklist');
      toast.error('Marca Cabina o ROPS/Toldo (al menos uno)');
      return;
    }

    // C-323b: «Observaciones» es una nota (ya no es obligatoria). Lo obligatorio es contestar «¿Hay daño?»;
    // con «Sí», cada daño con categoría y descripción. Se lee lo guardado (arriba se exige guardar antes).
    // C-323d: cada daño con tipo, ubicación y foto; con estado «Malo», al menos un daño
    const catalogos = await cargarCatalogosDanos().catch(() => null);
    const exige = new Set([...(catalogos?.tipos || []), ...(catalogos?.ubicaciones || [])]
      .filter((o) => o.exige_descripcion).map((o) => Number(o.id)));
    const guardados = (Array.isArray(r.danos_detalle) ? r.danos_detalle : [])
      .map((d) => ({ ...d, _exige: exige.has(Number(d.tipo_dano_id)) || exige.has(Number(d.ubicacion_id)) }));
    const errorDanos = validarDanosRecepcion({
      hay_dano: r.hay_dano === null || r.hay_dano === undefined ? null : Number(r.hay_dano),
      danos_detalle: guardados
    }, { exigirRespuesta: true, exigirFoto: true, estadoGeneral: r.estado_general });
    if (errorDanos) {
      this.showTab('observaciones');
      toast.error(errorDanos);
      document.getElementById('editDanosBloque')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Validar fotos de identificación
    if (!r.foto_num_serie_url) {
      this.showTab('evidencias');
      toast.error('Falta la foto del número de serie');
      return;
    }
    if (!r.foto_num_motor_url) {
      this.showTab('evidencias');
      toast.error('Falta la foto del número de motor');
      return;
    }
    if (!r.foto_horometro_url) {
      this.showTab('evidencias');
      toast.error('Falta la foto del horómetro');
      return;
    }

    // Fotos del tractor: opcionales (regla de negocio actualizada 2026-05-20).
    if (!r.video_url) {
      this.showTab('evidencias');
      toast.error('Agregue el video de inspección');
      return;
    }

    // Validar firmas
    if (!r.responsable_almacen_firma_url || !r.recibio_firma_url) {
      this.showTab('firmas');
      toast.error('Faltan firmas obligatorias');
      return;
    }

    // Pedimento obligatorio para compras (no aplica en reingresos)
    if (r.tipo_recepcion !== 'reingreso' && !r.pedimento) {
      toast.error('Falta el número de pedimento');
      const input = document.getElementById('editPedimento');
      if (input) {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus();
      }
      return;
    }

    if (!confirm('Una vez completada, la recepción será de solo lectura. ¿Continuar?')) return;

    try {
      const resultado = await recepcionService.completar(r.id);
      this._clearUnsaved();
      toast.success('Recepción completada');

      // Si se creó un PNC automáticamente (estado malo/regular), mostrar modal.
      // Si el usuario tiene permiso de ver PNC, lo redirige; si no, solo informa.
      const pncId = resultado?.pnc_id;
      if (pncId) {
        const puedeVerPnc = permisos.canView('pnc');
        if (puedeVerPnc) {
          modal.open(
            '⚠️ Producto No Conforme detectado',
            `
              <div style="padding: 0.5rem 0;">
                <p style="margin: 0 0 0.75rem; font-size: 0.95rem;">
                  Se creó el <strong>PNC-${pncId}</strong> con los daños que marcaste en «¿Hay daño?».
                </p>
                <p style="margin: 0; font-size: 0.9rem; color: var(--gray-600);">
                  Serás redirigido al PNC para que se completen los datos pendientes: <strong>acción</strong> y <strong>responsable de producción</strong>.
                </p>
              </div>
            `,
            [{
              text: 'Entendido, ir al PNC',
              class: 'btn-primary',
              onClick: () => router.navigate(`/calidad/pnc/${pncId}`)
            }],
            { dismissable: false }
          );
          return;
        } else {
          modal.open(
            '⚠️ Producto No Conforme detectado',
            `
              <div style="padding: 0.5rem 0;">
                <p style="margin: 0 0 0.75rem; font-size: 0.95rem;">
                  Se creó el <strong>PNC-${pncId}</strong> con los daños que marcaste en «¿Hay daño?».
                </p>
                <p style="margin: 0; font-size: 0.9rem; color: var(--gray-600);">
                  El equipo de calidad se encargará de completar los datos pendientes (acción y responsable de producción).
                </p>
              </div>
            `,
            [{
              text: 'Entendido',
              class: 'btn-primary',
              onClick: () => modal.close()
            }],
            { dismissable: false }
          );
          await this.cargarRecepcion(r.id);
          return;
        }
      }

      await this.cargarRecepcion(r.id);
    } catch (error) {
      const msg = error.response?.data?.message || error.data?.message || error.message || 'Error al completar la recepción';
      toast.error(msg);
    }
  },

  initEditEvents() {
    // Inicializar fotos de identificación (subida vía mediaChooser, ver pickFotoIdentificacion)
    this.data.fotoNumSerieNueva = null;
    this.data.fotoNumMotorNueva = null;
    this.data.fotoHorometroNueva = null;

    // Normalizar pedimento (solo dígitos, sin espacios).
    const pedimentoInput = document.getElementById('editPedimento');
    if (pedimentoInput) attachNormalizer(pedimentoInput, normalizers.numero);

    // Renderizar grid de fotos
    this.renderEditFotosGrid();

    // Renderizar y inicializar firmas
    this._renderFirmasDinamicas();
    this.initFirmasEdicion();

    // Toggle del asterisco "Observaciones" + warning si baja la severidad con texto escrito
    const selEstado = document.getElementById('editEstadoGeneral');
    if (selEstado) {
      selEstado._prevValue = selEstado.value;
      selEstado.addEventListener('change', () => {
        const prev = selEstado._prevValue;
        const nuevo = selEstado.value;
        const setAsterisco = () => {};
        // C-323b: las observaciones ya no se borran al cambiar el estado general (son una nota, no el PNC)
        const txt = document.getElementById('editObservaciones');
        const haTexto = false;
        if (haTexto && ['regular','malo'].includes(prev) && ['bueno','excelente'].includes(nuevo)) {
          const etiquetaNuevo = nuevo === 'bueno' ? 'Bueno' : 'Excelente';
          modal.open(
            'Se eliminarán las observaciones',
            `<p style="margin:0;">Al cambiar el estado a <strong>${etiquetaNuevo}</strong>, las observaciones registradas se eliminarán. ¿Deseas continuar?</p>`,
            [
              { text: 'Cancelar', class: 'btn-secondary', onClick: () => {
                selEstado.value = prev;
                setAsterisco(prev);
                // _prevValue NO se actualiza: seguimos en el estado original
              }},
              { text: 'Continuar', class: 'btn-primary', onClick: () => {
                txt.value = '';
                this._showUnsaved();
                selEstado._prevValue = nuevo;
              }}
            ]
          );
          // Importante: NO actualizar _prevValue aquí (el usuario aún no decide)
          return;
        }
        selEstado._prevValue = nuevo;
      });
    }

    // C-323b: «¿Hay daño?» (los renglones nuevos también marcan cambios sin guardar)
    iniciarDanosRecepcion('edit', () => this._showUnsaved(), { numSerie: () => this.data?.recepcion?.num_serie || '' });

    // Detectar cambios sin guardar
    document.querySelectorAll('#recContent input, #recContent textarea, #recContent select').forEach(el => {
      el.addEventListener('change', () => this._showUnsaved());
      el.addEventListener('input', () => this._showUnsaved());
    });

    this._beforeUnloadHandler = (e) => {
      if (this._hasUnsaved) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', this._beforeUnloadHandler);
  },

  initFirmasEdicion() {
    if (typeof SignaturePad === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/signature_pad/4.1.7/signature_pad.umd.min.js';
      script.onload = () => this._setupSignaturePads();
      document.head.appendChild(script);
    } else {
      this._setupSignaturePads();
    }
  },

  _setupSignaturePads() {
    const canvasAlmacen = document.getElementById('firma-almacen-edit');
    const canvasRecibio = document.getElementById('firma-recibio-edit');

    const setupCanvas = (canvas) => {
      if (!canvas) return null;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      const pad = new SignaturePad(canvas, { backgroundColor: 'rgb(255, 255, 255)', penColor: 'rgb(0, 0, 0)' });
      canvas.addEventListener('pointerdown', () => {
        const ph = canvas.parentElement.querySelector('.firma-placeholder');
        if (ph) ph.style.display = 'none';
      });
      return pad;
    };

    this.signaturePadAlmacen = setupCanvas(canvasAlmacen);
    this.signaturePadRecibio = setupCanvas(canvasRecibio);
  },

  _renderFirmasDinamicas() {
    const container = document.getElementById('firmasContainer');
    if (!container) return;
    const r = this.data.recepcion;

    if (r.completada) {
      container.style.gap = '3rem';
      container.innerHTML = `
        ${this._renderFirmaLectura('Elaboró', r.responsable_almacen_nombre, r.responsable_almacen_firma_url)}
        ${this._renderFirmaLectura('Jefe de Almacén', r.recibio_nombre, r.recibio_firma_url)}
      `;
    } else {
      container.innerHTML = `
        ${this._renderFirmaEdicion('almacen', 'Elaboró', r.responsable_almacen_nombre, r.responsable_almacen_firma_url)}
        ${this._renderFirmaEdicion('recibio', 'Jefe de Almacén', r.recibio_nombre, r.recibio_firma_url)}
      `;
    }
  },

  _renderFirmaLectura(label, nombre, url) {
    return `
      <div style="text-align: center; padding: 0.5rem;">
        <div style="min-height: 150px; display: flex; align-items: center; justify-content: center;">
          ${url
            ? `<img src="${url}" style="width: 100%; max-height: 150px; object-fit: contain;" onerror="this.style.display='none'" />`
            : '<span style="color: var(--text-secondary);">Sin firma</span>'
          }
        </div>
        <hr style="border: none; border-top: 3px solid #000; margin: 0.5rem 0;" />
        <p style="margin: 0.25rem 0 0; color: var(--text-secondary); font-size: 0.85rem;">${label}</p>
      </div>
    `;
  },

  _renderFirmaEdicion(tipo, label, nombre, existingUrl) {
    if (existingUrl) {
      return `
        <div style="border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; background: white;">
          <div style="background: var(--bg-secondary); padding: 0.6rem; text-align: center; border-bottom: 1px solid var(--gray-200);">
            <span style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); font-weight: 600;">${label}</span>
          </div>
          <div style="padding: 1.5rem; text-align: center;">
            <img src="${existingUrl}" style="width: 100%; max-height: 100px; object-fit: contain;" onerror="this.style.display='none'" />
          </div>
          <div style="padding: 0 1rem 0.25rem; text-align: center;">
            <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary);" onclick="window.detalleRecepcionView.cambiarFirmaExistente('${tipo}')">Cambiar firma</a>
          </div>
        </div>
      `;
    }
    return `
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; background: white;">
        <div style="background: var(--bg-secondary); padding: 0.6rem; text-align: center; border-bottom: 1px solid var(--gray-200);">
          <span style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); font-weight: 600;">${label}</span>
        </div>
        <div class="firma-box" style="position: relative;">
          <canvas id="firma-${tipo}-edit" class="firma-canvas-new"></canvas>
          <span class="firma-placeholder" id="firma-placeholder-${tipo}" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; pointer-events: none; color: var(--gray-400);">
            <span style="font-size: 0.8rem;">Firme aquí</span>
          </span>
        </div>
        <div style="padding: 0 1rem 0.25rem; text-align: center;">
          <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary);" onclick="window.detalleRecepcionView.limpiarFirma('${tipo}')">Limpiar</a>
        </div>
      </div>
    `;
  },

  cambiarFirmaExistente(tipo) {
    const r = this.data.recepcion;
    // Limpiar la URL existente para mostrar canvas
    if (tipo === 'almacen') r.responsable_almacen_firma_url = null;
    if (tipo === 'recibio') r.recibio_firma_url = null;
    this._renderFirmasDinamicas();
    this.initFirmasEdicion();
    this._showUnsaved();
  },

  limpiarFirma(tipo) {
    const canvasId = `firma-${tipo}-edit`;
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;

    // Re-dimensionar canvas
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);

    // Re-crear SignaturePad
    const pad = new SignaturePad(canvas, { backgroundColor: 'rgb(255, 255, 255)', penColor: 'rgb(0, 0, 0)' });
    canvas.addEventListener('pointerdown', () => {
      const ph = document.getElementById(`firma-placeholder-${tipo}`);
      if (ph) ph.style.display = 'none';
    });

    if (tipo === 'almacen') this.signaturePadAlmacen = pad;
    if (tipo === 'recibio') this.signaturePadRecibio = pad;

    // Mostrar placeholder
    const ph = document.getElementById(`firma-placeholder-${tipo}`);
    if (ph) ph.style.display = '';
  },

  renderEditCheckItem(name, label, value) {
    return `
      <label class="checkbox-label">
        <input type="checkbox" name="chk_${name}" ${value ? 'checked' : ''} />
        <span>${label}</span>
      </label>
    `;
  },

  renderEditFotosGrid() {
    const grid = document.getElementById('edit-fotos-grid');
    if (!grid) return;
    grid.innerHTML = '';

    const r = this.data.recepcion;
    const fotosExistentes = r.fotos || [];
    fotosExistentes.forEach((foto, idx) => {
      const div = document.createElement('div');
      div.className = 'foto-item';
      div.onclick = () => this.verFoto(foto, idx + 1);
      div.innerHTML = `
        <img src="${foto}" alt="Foto ${idx + 1}" onerror="this.src='data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22%3E%3Crect fill=%22%23ddd%22 width=%22200%22 height=%22200%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 fill=%22%23999%22 text-anchor=%22middle%22 dy=%22.3em%22%3EFoto ${idx + 1}%3C/text%3E%3C/svg%3E'" />
        <div class="foto-overlay"><span>📷 Foto ${idx + 1}</span></div>
      `;
      grid.appendChild(div);
    });

    // Fotos nuevas (pendientes de subir)
    this.data.fotosNuevas.forEach((file, idx) => {
      const div = document.createElement('div');
      div.className = 'foto-item';
      div.style.border = '2px solid var(--primary)';
      const reader = new FileReader();
      reader.onload = (e) => {
        div.innerHTML = `
          <img src="${e.target.result}" alt="Nueva ${idx + 1}">
          <button class="foto-remove" type="button" onclick="event.stopPropagation(); window.detalleRecepcionView.removeNuevaFoto(${idx})">×</button>
        `;
      };
      reader.readAsDataURL(file);
      grid.appendChild(div);
    });

    // Placeholders vacíos (mínimo 7 total)
    const totalFotos = fotosExistentes.length + this.data.fotosNuevas.length;
    const placeholdersNeeded = Math.max(0, 7 - totalFotos);
    for (let i = 0; i < placeholdersNeeded; i++) {
      const ph = document.createElement('div');
      ph.className = 'foto-placeholder';
      ph.innerHTML = '<span class="foto-placeholder-icon">📷</span>';
      ph.onclick = () => this.pickFotosTractor();
      grid.appendChild(ph);
    }

    // Botón + para agregar más
    const addBtn = document.createElement('div');
    addBtn.className = 'foto-add-btn';
    addBtn.innerHTML = '+';
    addBtn.onclick = () => this.pickFotosTractor();
    grid.appendChild(addBtn);
  },

  removeNuevaFoto(index) {
    this.data.fotosNuevas.splice(index, 1);
    this.renderEditFotosGrid();
    this._showUnsaved();
  },

  async pickFotoIdentificacion(tipo) {
    if (this.data.recepcion && this.data.recepcion.completada) {
      toast.error('No se puede editar una recepción completada');
      return;
    }
    const file = await pickImage();
    if (!file) return;
    const compressed = await compressImage(file);
    if (tipo === 'numserie') this.data.fotoNumSerieNueva = compressed;
    else if (tipo === 'nummotor') this.data.fotoNumMotorNueva = compressed;
    else if (tipo === 'horometro') this.data.fotoHorometroNueva = compressed;
    const box = document.getElementById(`foto-${tipo}-box`);
    const reader = new FileReader();
    reader.onload = (ev) => {
      box.innerHTML = `<img src="${ev.target.result}" style="width: 100%; height: 100%; object-fit: cover;" />`;
      box.style.border = '2px solid var(--primary)';
    };
    reader.readAsDataURL(compressed);
    this._showUnsaved();
  },

  async pickFotosTractor() {
    if (this.data.recepcion && this.data.recepcion.completada) {
      toast.error('No se puede editar una recepción completada');
      return;
    }
    const result = await pickImage({ multiple: true });
    if (!result) return;
    const files = Array.isArray(result) ? result : [result];

    for (const file of files) {
      const compressed = await compressImage(file);
      this.data.fotosNuevas.push(compressed);
    }

    if (files.length > 0) {
      toast.success(`${files.length} foto(s) agregada(s)`);
      this.renderEditFotosGrid();
      this._showUnsaved();
    }
  },

  async pickVideoInspeccion() {
    const r = this.data.recepcion;
    if (r && r.completada) {
      toast.error('No se puede editar una recepción completada');
      return;
    }
    // Misma lógica (selector + validación + subida + guardado) que el listado
    // y el kebab de la OC: una sola fuente de verdad en utils/videoUploader.js
    const ok = await subirVideoRecepcion(r.id, r.num_serie, {
      onProgress: (msg) => this._showProgress(msg),
      existeVideo: !!r.video_url
    });
    this._hideProgress();
    if (ok) await this.cargarRecepcion(r.id);
  },

  async guardarCambios() {
    if (this.data.recepcion.completada) {
      toast.error('No se puede editar una recepción completada');
      return;
    }

    const btn = document.getElementById('guardarEdicionBtn');
    btn.disabled = true;

    // Mostrar overlay de progreso
    this._showProgress('Preparando datos...');

    try {
      const r = this.data.recepcion;
      const data = {};

      // Checklist
      const checklistFields = [
        'documentacion', 'emblema', 'salpicaderas', 'llantas_traseras', 'llantas_delanteras',
        'caja_herramientas', 'caja_tornilleria', 'bastidor_bateria', 'eje_delantero', 'cubierta_pto',
        'tren_motriz', 'estabilizadores', 'mofle_escape', 'barra_tiro', 'brazos_levante',
        'contrapesos_traseros', 'contrapesos_delanteros', 'rops_toldo', 'cabina'
      ];
      data.checklist = {};
      checklistFields.forEach(name => {
        data.checklist[name] = document.querySelector(`[name="chk_${name}"]`)?.checked || false;
      });

      // Observaciones
      data.estado_general = document.getElementById('editEstadoGeneral')?.value || 'bueno';
      data.observaciones = (document.getElementById('editObservaciones')?.value || '').toUpperCase();
      // C-323b: «¿Hay daño?» y sus renglones
      const danosRec = leerDanosRecepcion('edit');
      const errorDanos = validarDanosRecepcion(danosRec);
      if (errorDanos) {
        this.showTab('observaciones');
        throw new Error(errorDanos);
      }
      data.hay_dano = danosRec.hay_dano;
      data.danos_detalle = danosParaGuardar(danosRec.danos_detalle);

      // Documentación
      data.certificado_origen = document.getElementById('editCertificadoOrigen')?.checked || false;
      data.guia_embarque = (document.getElementById('editGuiaEmbarque')?.value || '').toUpperCase();
      data.factura = (document.getElementById('editFactura')?.value || '').toUpperCase();
      data.pedimento = (document.getElementById('editPedimento')?.value || '').toUpperCase();
      data.fecha_recepcion = document.getElementById('editFechaRecepcion')?.value || '';

      // Subir fotos de identificación
      if (this.data.fotoNumSerieNueva) {
        this._showProgress('Subiendo foto número de serie...');
        data.foto_num_serie_url = await recepcionService.subirFoto(
          this.data.fotoNumSerieNueva,
          `${r.num_serie}_num_serie.jpg`,
          r.num_serie
        );
      }
      if (this.data.fotoNumMotorNueva) {
        this._showProgress('Subiendo foto número de motor...');
        data.foto_num_motor_url = await recepcionService.subirFoto(
          this.data.fotoNumMotorNueva,
          `${r.num_serie}_num_motor.jpg`,
          r.num_serie
        );
      }
      if (this.data.fotoHorometroNueva) {
        this._showProgress('Subiendo foto del horómetro...');
        data.foto_horometro_url = await recepcionService.subirFoto(
          this.data.fotoHorometroNueva,
          `${r.num_serie}_horometro.jpg`,
          r.num_serie
        );
      }

      // Subir firmas nuevas
      if (this.signaturePadAlmacen && !this.signaturePadAlmacen.isEmpty()) {
        this._showProgress('Subiendo firmas...');
        const blob = await this.toBlob(this.signaturePadAlmacen);
        data.responsable_almacen_firma_url = await recepcionService.subirFirma(blob, `${r.num_serie}_firma_almacen.png`, r.num_serie);
      }
      if (this.signaturePadRecibio && !this.signaturePadRecibio.isEmpty()) {
        const blob = await this.toBlob(this.signaturePadRecibio);
        data.recibio_firma_url = await recepcionService.subirFirma(blob, `${r.num_serie}_firma_recibio.png`, r.num_serie);
      }

      // Subir fotos nuevas
      if (this.data.fotosNuevas.length > 0) {
        this._showProgress('Subiendo fotos del tractor...');
        const nuevasUrls = await recepcionService.subirMultiplesFotos(
          this.data.fotosNuevas,
          r.num_serie,
          (progress) => { this._showProgress(`Subiendo fotos... ${progress.percentage}%`); }
        );
        data.fotos = [...(r.fotos || []), ...nuevasUrls];
      }

      // El video ya NO se sube aquí: tiene su propio flujo/permiso
      // (`subir_video`) y se persiste al momento de seleccionarlo.

      this._showProgress('Guardando recepción...');
      await recepcionService.actualizar(r.id, data);
      this._hideProgress();
      this._clearUnsaved();
      toast.success('Recepción actualizada exitosamente');

      // Recargar datos
      await this.cargarRecepcion(r.id);

    } catch (error) {
      console.error('Error al guardar:', error);
      this._hideProgress();
      toast.error(error.message || 'Error al guardar cambios');
    } finally {
      btn.disabled = false;
      btn.innerHTML = 'Guardar Cambios';
    }
  },

  async toBlob(signaturePad) {
    const dataURL = signaturePad.toDataURL('image/png');
    const response = await fetch(dataURL);
    return response.blob();
  },

  showTab(tab) {
    document.querySelectorAll('.rec-tab-content').forEach(el => el.style.display = 'none');
    const target = document.getElementById(`tab-${tab}`);
    if (target) target.style.display = 'block';

    document.querySelectorAll('.rec-tab').forEach(btn => btn.classList.remove('active'));
    document.querySelector(`.rec-tab[onclick*="'${tab}'"]`)?.classList.add('active');

    // Re-dimensionar canvas de firmas al cambiar a ese tab
    if (tab === 'firmas') {
      setTimeout(() => {
        ['firma-almacen-edit', 'firma-recibio-edit'].forEach(id => {
          const canvas = document.getElementById(id);
          if (!canvas) return;
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const width = canvas.offsetWidth;
          const height = canvas.offsetHeight;
          canvas.width = width * ratio;
          canvas.height = height * ratio;
          canvas.getContext('2d').scale(ratio, ratio);
        });
        // Re-crear los pads con el canvas ya dimensionado
        this._setupSignaturePads();
      }, 50);
    }
  },

  getBadgeClass(estado) {
    const classes = { 'excelente': 'success', 'bueno': 'info', 'regular': 'warning', 'malo': 'danger' };
    return classes[estado] || 'secondary';
  },

  verFoto(url, numero) {
    const modalHtml = `
      <div class="modal-overlay" onclick="this.remove()" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.9); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 2rem;">
        <div style="max-width: 90vw; max-height: 90vh; position: relative;" onclick="event.stopPropagation()">
          <button onclick="this.closest('.modal-overlay').remove()" style="position: absolute; top: -40px; right: 0; background: white; border: none; width: 32px; height: 32px; border-radius: 50%; cursor: pointer; font-size: 1.5rem; line-height: 1;">×</button>
          <img src="${url}" alt="Foto ${numero}" style="max-width: 100%; max-height: 90vh; border-radius: 8px;">
          <p style="text-align: center; color: white; margin-top: 1rem;">Foto ${numero}</p>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
  },

  imprimir(id) {
    router.navigate(`/almacen/recepciones/${id}/print`);
  },

  volver() {
    if (this._hasUnsaved) {
      if (!confirm('Hay cambios sin guardar. ¿Está seguro de salir?')) return;
    }
    this._clearUnsaved();
    router.navigate('/almacen/recepciones');
  },

  _disableForm() {
    // Deshabilitar todos los inputs, selects, textareas, checkboxes
    document.querySelectorAll('#recContent input, #recContent select, #recContent textarea').forEach(el => {
      el.disabled = true;
      el.style.opacity = '0.7';
      el.style.cursor = 'not-allowed';
    });

    // Ocultar botones de acción (Guardar, Completar, Limpiar firmas)
    const botones = document.querySelector('#recContent .rec-tab-content:last-of-type')?.nextElementSibling;
    const botonesAccion = document.getElementById('guardarEdicionBtn')?.closest('div');
    if (botonesAccion) botonesAccion.style.display = 'none';

    // Ocultar indicador de cambios
    const unsaved = document.getElementById('unsavedIndicator');
    if (unsaved) unsaved.style.display = 'none';

    // Ocultar botones de agregar fotos, video, limpiar firma
    document.querySelectorAll('#recContent .foto-add-btn, #recContent .foto-placeholder').forEach(el => {
      el.style.display = 'none';
    });
    document.querySelectorAll('#recContent a[onclick*="limpiarFirma"], #recContent a[onclick*="cambiarFirma"]').forEach(el => {
      el.style.display = 'none';
    });

    // Ocultar placeholder "Firme aquí"
    document.querySelectorAll('#recContent .firma-placeholder').forEach(el => {
      el.style.display = 'none';
    });

    // Ocultar placeholder de video (la captura ya no permite cambios)
    document.querySelectorAll('#recContent .foto-placeholder[onclick*="pickVideoInspeccion"]').forEach(el => {
      el.style.display = 'none';
    });
  },

  _showProgress(message) {
    let overlay = document.getElementById('progressOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'progressOverlay';
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.9); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 9999;';
      overlay.innerHTML = `
        <div class="spinner" style="margin-bottom: 1rem;"></div>
        <p id="progressMessage" style="font-size: 1rem; font-weight: 600; color: var(--gray-700);"></p>
      `;
      document.body.appendChild(overlay);
    }
    document.getElementById('progressMessage').textContent = message;
  },

  _hideProgress() {
    const overlay = document.getElementById('progressOverlay');
    if (overlay) overlay.remove();
  },

  _showUnsaved() {
    this._hasUnsaved = true;
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  _clearUnsaved() {
    this._hasUnsaved = false;
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'none';
    if (this._beforeUnloadHandler) window.removeEventListener('beforeunload', this._beforeUnloadHandler);
  }
};
